import React, { useEffect, useState, useRef } from 'react';
import { documentosService, API_BASE } from '../services/api';

const TIPOS_DOCUMENTO = [
  'contrato_arrendamiento',
  'manual_uso',
  'plano',
  'notarial',
  'titulo',
  'contrato_conexion',
  'inventario',
  'foto',
  'otro',
];

const TIPO_LABELS = {
  contrato_arrendamiento: 'Contrato de Arrendamiento',
  manual_uso: 'Manual de Uso',
  plano: 'Plano',
  notarial: 'Notarial / Título',
  titulo: 'Título de Propiedad',
  contrato_conexion: 'Contrato de Conexión',
  inventario: 'Inventario',
  foto: 'Foto',
  otro: 'Otro',
};

const TIPO_ICONOS = {
  contrato_arrendamiento: '📄',
  manual_uso: '📘',
  plano: '📐',
  notarial: '⚖️',
  titulo: '🏛',
  contrato_conexion: '🔌',
  inventario: '📋',
  foto: '📷',
  otro: '📎',
};

export default function Documentos({ entidadTipo, entidadId }) {
  const [documentos, setDocumentos] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [form, setForm] = useState({ tipo: 'contrato_arrendamiento', nombre: '', descripcion: '' });
  const [archivo, setArchivo] = useState(null);
  const [arrastrando, setArrastrando] = useState(false);
  const inputRef = useRef();

  const cargar = React.useCallback(() => documentosService.getByEntidad(entidadTipo, entidadId).then(r => setDocumentos(r.data)), [entidadTipo, entidadId]);
  useEffect(() => { cargar(); }, [cargar]);

  const handleArchivo = (file) => {
    setArchivo(file);
    if (!form.nombre) setForm(f => ({ ...f, nombre: file.name.replace(/\.[^/.]+$/, '') }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setArrastrando(false);
    const file = e.dataTransfer.files[0];
    if (file) handleArchivo(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!archivo) return;
    setSubiendo(true);
    const fd = new FormData();
    fd.append('archivo', archivo);
    fd.append('entidad_tipo', entidadTipo);
    fd.append('entidad_id', entidadId);
    fd.append('tipo', form.tipo);
    fd.append('nombre', form.nombre);
    fd.append('descripcion', form.descripcion);
    await documentosService.subir(fd);
    setForm({ tipo: 'contrato_arrendamiento', nombre: '', descripcion: '' });
    setArchivo(null);
    setSubiendo(false);
    cargar();
  };

  const handleEliminar = async (id, nombre) => {
    if (window.confirm(`¿Eliminar "${nombre}"?`)) {
      await documentosService.eliminar(id);
      cargar();
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ fontSize: '1rem', color: '#1a1a2e', marginBottom: 16 }}>Documentos adjuntos</h3>

      {/* Zona de carga */}
      <div
        onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current.click()}
        style={{
          border: `2px dashed ${arrastrando ? '#4f8ef7' : '#ddd'}`,
          borderRadius: 10,
          padding: '20px',
          textAlign: 'center',
          cursor: 'pointer',
          background: arrastrando ? '#f0f6ff' : '#fafafa',
          transition: 'all 0.2s',
          marginBottom: 16,
        }}
      >
        <input ref={inputRef} type="file" style={{ display: 'none' }}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
          onChange={e => e.target.files[0] && handleArchivo(e.target.files[0])}
        />
        {archivo ? (
          <p style={{ color: '#4f8ef7', fontSize: '0.9rem' }}>📎 {archivo.name} <span style={{ color: '#aaa' }}>({formatBytes(archivo.size)})</span></p>
        ) : (
          <>
            <p style={{ color: '#888', fontSize: '0.9rem' }}>Arrastrá un archivo acá o hacé clic para seleccionar</p>
            <p style={{ color: '#bbb', fontSize: '0.78rem', marginTop: 4 }}>PDF, JPG, PNG, DOC, XLS — máx. 50MB</p>
          </>
        )}
      </div>

      {/* Formulario de carga */}
      {archivo && (
        <form onSubmit={handleSubmit} style={{ background: '#f8f9fa', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div className="form-grid">
            <div className="form-group">
              <label>Tipo de documento</label>
              <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                {TIPOS_DOCUMENTO.map(t => <option key={t} value={t}>{TIPO_LABELS[t]}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Nombre</label>
              <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} required />
            </div>
            <div className="form-group full">
              <label>Descripción (opcional)</label>
              <input value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="button" className="btn btn-secondary" onClick={() => { setArchivo(null); setForm({ tipo: 'contrato_arrendamiento', nombre: '', descripcion: '' }); }}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={subiendo}>
              {subiendo ? 'Subiendo...' : '⬆️ Subir documento'}
            </button>
          </div>
        </form>
      )}

      {/* Lista de documentos */}
      {documentos.length === 0 ? (
        <p style={{ color: '#bbb', fontSize: '0.85rem', textAlign: 'center', padding: '16px 0' }}>No hay documentos adjuntos</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {documentos.map(doc => (
            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'white', borderRadius: 8, padding: '10px 14px', border: '1px solid #f0f0f0' }}>
              <span style={{ fontSize: '1.3rem' }}>{TIPO_ICONOS[doc.tipo] || '📎'}</span>
              <div style={{ flex: 1 }}>
                <a href={doc.archivo_url.startsWith('http') ? doc.archivo_url : `${API_BASE}${doc.archivo_url}`} target="_blank" rel="noreferrer"
                  style={{ color: '#1a1a2e', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none' }}>
                  {doc.nombre}
                </a>
                <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                  <span style={{ fontSize: '0.75rem', color: '#aaa' }}>{TIPO_LABELS[doc.tipo]}</span>
                  {doc.archivo_tamanio && <span style={{ fontSize: '0.75rem', color: '#ccc' }}>· {formatBytes(doc.archivo_tamanio)}</span>}
                  {doc.descripcion && <span style={{ fontSize: '0.75rem', color: '#aaa' }}>· {doc.descripcion}</span>}
                </div>
              </div>
              <button onClick={() => handleEliminar(doc.id, doc.nombre)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontSize: '1rem', padding: '4px' }}>🗑</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
